import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "../../store";
import {
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import JobType from "../../types/jobType";
import { Inputs } from "../formInputs";
import * as Icons from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";
import { Base } from "../base";
import DashboardItem from "../base/DashboardItem";
import ManageJobTypeKeyValueOptionForm from "../forms/ManageJobTypeKeyValueOptionForm";
import { editJobType, postJobType } from "../../slices/jobType";
import theme from "../../theme";
import IconSwitcher, { FilledIconName } from "../base/IconSwitcher";
import { getAnswerTypes } from "../../slices/options";
import Question, { AnswerTypes } from "../../types/questionVM";
import QuestionPreview, {
  CustomJobType,
} from "../views/JobType/QuestionPreview";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortItem from "../views/JobType/SortableItem";
import ManageJobTypeStatusForm from "../forms/ManageJobTypeStatusForm";
import JobQuestionSearchView from "../views/JobType/JobQuestionSearchView";
import Switch from "../base/Switch";

const EditJobTypePage: React.FC = () => {
  const { id } = useParams();
  const { jobType, status } = useSelector((store) => store.jobType);
  const { answerTypes } = useSelector((store) => store.options);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [color, setColor] = useState(jobType.color);
  const [statusModal, setStatusModal] = useState(false);
  const [colorModal, setColorModal] = useState(false);
  const [order, setOrder] = useState<number>(jobType.questions.length + 1);
  const [multipleChoiceIndex, setMultipleChoiceIndex] = useState<number>(-1);
  const excluded = ["Outlined", "Rounded", "Sharp", "TwoTone"];
  const iconOptions = Object.keys(Icons)
    .filter((icon) => {
      return !excluded.some((suffix) => icon.endsWith(suffix));
    })
    .map((iconName) => ({
      key: iconName,
      value: iconName,
    }));

  useEffect(() => {
    if (jobType.id !== +id!) {
      navigate("/settings");
    }
  }, [jobType, id, navigate]);

  useEffect(() => {
    dispatch(getAnswerTypes());
  }, [dispatch]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required").typeError("Required"),
    icon: Yup.string().required("Required").typeError("Required"),
  });

  //make local typeahead
  const initialValues: CustomJobType = {
    ...jobType,
    icon:
      jobType.id > 0
        ? Object.keys(Icons)
            .filter((icon) => {
              return !excluded.some((suffix) => icon.endsWith(suffix));
            })
            .filter((iconName) => iconName === jobType.icon)
            .map((i) => i)[0]
        : undefined,
    statusAdd: {
      id: 0,
      iconColor: "",
      icon: "",
      name: "",
      order: jobType.statuses.length + 1,
    },
    highlight: false,
    start: new Date(),
    milestoneTitle: "",
  };

  const handleSubmit = (values: JobType) => {
    const orderedQuestions = values.questions.map((q, i) => {
      return {
        ...q,
        order: i + 1,
      };
    });
    const orderedStatuses = values.statuses.map((s, i) => {
      return {
        ...s,
        order: i + 1,
      };
    });
    if (values.id === 0) {
      dispatch(
        postJobType({
          ...values,
          questions: orderedQuestions,
          statuses: orderedStatuses,
        }),
      ).then(({ meta }) => {
        if (meta.requestStatus === "fulfilled") {
          navigate("/settings");
        }
      });
    } else {
      dispatch(
        editJobType({
          ...values,
          questions: orderedQuestions,
          statuses: orderedStatuses,
        }),
      ).then(({ meta }) => {
        if (meta.requestStatus === "fulfilled") {
          navigate("/settings");
        }
      });
    }
  };

  const renderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => {
    return (
      <li
        {...props}
        key={option.value}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <Typography
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconSwitcher
            name={option.value as FilledIconName}
            style={{ fontSize: 25 }}
          />
          {option.value}
        </Typography>
      </li>
    );
  };

  const statusError = "At least one status/type is required";
  const questionTextError = "All non-section questions must have question text";
  const multipleChoiceError =
    "Multiple choice questions must have at least one choice";

  return (
    <Container maxWidth="xl" sx={{ my: 3 }}>
      <Paper sx={{ paddingY: 2, paddingX: { xs: 2, md: 5 }, marginBottom: 5 }}>
        {status === "loading" ? (
          <CircularProgress />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(v, { setStatus }) => {
              if (!v.statuses.length) {
                setStatus(statusError);
              } else if (
                v.questions
                  .filter((q) => q.answerType.id !== AnswerTypes.Section)
                  .some((q) => !q.questionText)
              ) {
                setStatus(questionTextError);
              } else if (
                v.questions
                  .filter((q) => q.answerType.id === AnswerTypes.MultipleChoice)
                  .some((q) => !q.choices.length)
              ) {
                setStatus(multipleChoiceError);
              } else {
                handleSubmit(v);
              }
            }}
          >
            {({ values, setFieldValue, status, setStatus }) => (
              <Form
                noValidate
                onChange={() => {
                  if (
                    (values.questions
                      .filter((q) => q.answerType.id !== AnswerTypes.Section)
                      .every((q) => !!q.questionText) &&
                      status === questionTextError) ||
                    (values.questions
                      .filter(
                        (q) => q.answerType.id === AnswerTypes.MultipleChoice,
                      )
                      .every((q) => !!q.choices.length) &&
                      status === multipleChoiceError)
                  ) {
                    setStatus(undefined);
                  }
                }}
              >
                <Grid container direction={"row"}>
                  <Grid size={{ md: 6, xs: 12 }} sx={{ px: 3 }}>
                    <Grid container direction={"row"} spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Field
                          component={Inputs.Text}
                          name="name"
                          label="Job Type Name"
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Field
                          component={Inputs.Dropdown}
                          name="icon"
                          label="Icon"
                          options={iconOptions}
                          staticFilter
                          required
                          onSelected={(e: any, v: any) => {
                            if (v && !!v.value) {
                              setFieldValue("icon", v.value);
                            }
                          }}
                          renderOption={renderOption}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Field
                          component={Inputs.Checkbox}
                          name="showRecentlyClosed"
                          label="Show Recently Closed"
                          checked={values.showRecentlyClosed}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Field
                          component={Inputs.Checkbox}
                          name="logHours"
                          label="Log Hours"
                          checked={values.logHours}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Base.Button
                          text="Manage Statuses"
                          onClick={() => {
                            setStatusModal(true);
                            if (status === statusError) {
                              setStatus(undefined);
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Base.Button
                          text="Change Color"
                          onClick={() => setColorModal(true)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Grid
                          container
                          direction={"row"}
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          <Grid>
                            <Base.Button
                              text="Cancel"
                              onClick={() => {
                                navigate("/settings");
                              }}
                              type="secondary"
                            />
                          </Grid>
                          <Grid>
                            <Inputs.Submit text="Save" isSubmitting={false} />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12}>
                        <Typography
                          textAlign={"center"}
                          variant="subtitle1"
                          color="error"
                        >
                          {status}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }} sx={{ px: 3 }}>
                    <Grid
                      container
                      direction={"column"}
                      alignItems={"center"}
                      flex={1}
                    >
                      <Grid size={12}>
                        <DashboardItem
                          jobType={values}
                          loading={false}
                          statusCounts={values.statuses.map((s) => {
                            return { jobStatus: s, count: 0 };
                          })}
                          example
                          closedTotal={0}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container direction={"row"} my={1}>
                  <Grid size={{ xs: 5 }}>
                    <Typography>Add Question:</Typography>
                    <Grid container spacing={1.5} py={2}>
                      {answerTypes.map((a) => (
                        <Grid key={a.id}>
                          <Base.Button
                            onClick={() => {
                              const newQuestion: Question = {
                                id: 0,
                                answerType: a,
                                questionText:
                                  a.id === AnswerTypes.Confidential
                                    ? "Confidential"
                                    : "",
                                required: false,
                                sensitiveData: false,
                                multipleChoice: false,
                                freeSolo: false,
                                choices: [],
                                filterSelectedJobStatuses: [],
                                order: order,
                                customStaticOptions: false,
                                sortByAscending: false,
                              };
                              setFieldValue("questions", [
                                ...values.questions,
                                newQuestion,
                              ]);
                              setOrder(order + 1);
                            }}
                            text={`${a.value}`}
                            disabled={
                              a.id === AnswerTypes.Confidential &&
                              values.questions.some(
                                (q) =>
                                  q.answerType.id === AnswerTypes.Confidential,
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FieldArray
                        name="questions"
                        render={({ move, remove }) => (
                          <DndContext
                            onDragEnd={(e: DragEndEvent) => {
                              const { over, active } = e;

                              if (!over) return;

                              const oldIndex = values.questions.indexOf(
                                values.questions.find(
                                  (f) => f.order === active.id,
                                )!,
                              );
                              const newIndex = values.questions.indexOf(
                                values.questions.find(
                                  (f) => f.order === over.id,
                                )!,
                              );

                              move(oldIndex, newIndex);
                            }}
                            collisionDetection={closestCenter}
                          >
                            <ul
                              style={{ width: "100%", margin: 0, padding: 0 }}
                            >
                              <Grid
                                sx={{ height: 511, overflow: "auto", pb: 5 }}
                                container
                                flex={1}
                                alignContent={"flex-start"}
                              >
                                <SortableContext
                                  items={values.questions.map((q) => q.order)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {values.questions.map((q, i) => (
                                    <SortItem
                                      question={q}
                                      removeQuestion={() => remove(i)}
                                      key={i}
                                    >
                                      <Grid
                                        container
                                        direction={"row"}
                                        justifyContent={"space-between"}
                                      >
                                        <Grid size={{ xs: 12 }}>
                                          <Divider sx={{ mb: 3 }} />
                                          <Grid
                                            container
                                            justifyContent={"space-between"}
                                          >
                                            {q.answerType.id !==
                                              AnswerTypes.Confidential && (
                                              <Grid size={{ xs: 12 }}>
                                                <Field
                                                  component={Inputs.Text}
                                                  name={`questions.${i}.questionText`}
                                                  label="Question Text"
                                                  required={
                                                    q.answerType.id !==
                                                    AnswerTypes.Section
                                                  }
                                                />
                                              </Grid>
                                            )}
                                            {q.answerType.id ===
                                              AnswerTypes.Job && (
                                              <Grid size={12}>
                                                <JobQuestionSearchView
                                                  i={i}
                                                  values={values}
                                                  setFieldValue={setFieldValue}
                                                />
                                              </Grid>
                                            )}
                                            {q.answerType.id ===
                                              AnswerTypes.Title && (
                                              <Grid size={12}>
                                                <Typography
                                                  sx={{
                                                    textDecoration: "underline",
                                                  }}
                                                >
                                                  Sort by
                                                </Typography>
                                                <Grid
                                                  container
                                                  alignItems={"center"}
                                                >
                                                  <Typography>
                                                    Descending
                                                  </Typography>
                                                  <Switch
                                                    label=""
                                                    onChange={(e, c) => {
                                                      setFieldValue(
                                                        `questions.${i}.sortByAscending`,
                                                        c,
                                                      );
                                                    }}
                                                    checked={
                                                      values.questions[i]
                                                        .sortByAscending
                                                    }
                                                  />
                                                  <Typography>
                                                    Ascending
                                                  </Typography>
                                                </Grid>
                                              </Grid>
                                            )}
                                            {q.answerType.id !==
                                              AnswerTypes.Status &&
                                              q.answerType.id !==
                                                AnswerTypes.WeeklyTask &&
                                              q.answerType.id !==
                                                AnswerTypes.Section &&
                                              q.answerType.id !==
                                                AnswerTypes.Dates &&
                                              q.answerType.id !==
                                                AnswerTypes.Checkbox &&
                                              q.answerType.id !==
                                                AnswerTypes.Confidential &&
                                              q.answerType.id !==
                                                AnswerTypes.Title && (
                                                <Grid>
                                                  <Field
                                                    component={Inputs.Checkbox}
                                                    label="Required"
                                                    name={`questions.${i}.required`}
                                                  />
                                                </Grid>
                                              )}
                                            {(q.answerType.id ===
                                              AnswerTypes.MultipleChoice ||
                                              q.answerType.id ===
                                                AnswerTypes.Job) && (
                                              <>
                                                <Grid>
                                                  <Field
                                                    component={Inputs.Checkbox}
                                                    label="Multiple?"
                                                    name={`questions.${i}.multipleChoice`}
                                                  />
                                                </Grid>
                                                <Grid>
                                                  <Field
                                                    component={Inputs.Checkbox}
                                                    label="Dynamic Entry?"
                                                    name={`questions.${i}.freeSolo`}
                                                  />
                                                </Grid>
                                                {q.answerType.id ===
                                                  AnswerTypes.Job && (
                                                  <Grid>
                                                    <Field
                                                      component={
                                                        Inputs.Checkbox
                                                      }
                                                      label="Additional Options?"
                                                      name={`questions.${i}.customStaticOptions`}
                                                      disabled={
                                                        q.choices.length
                                                      }
                                                    />
                                                  </Grid>
                                                )}
                                                {(q.answerType.id ===
                                                  AnswerTypes.MultipleChoice ||
                                                  (q.answerType.id ===
                                                    AnswerTypes.Job &&
                                                    q.customStaticOptions)) && (
                                                  <Grid>
                                                    <Base.Button
                                                      onClick={() => {
                                                        setMultipleChoiceIndex(
                                                          i,
                                                        );
                                                      }}
                                                      text="Manage Choices"
                                                    />
                                                  </Grid>
                                                )}
                                              </>
                                            )}
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    </SortItem>
                                  ))}
                                </SortableContext>
                              </Grid>
                            </ul>
                          </DndContext>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 7 }} px={3}>
                    <Typography align="center" sx={{ my: 1 }}>
                      Preview
                    </Typography>
                    <Grid
                      container
                      direction={"row"}
                      spacing={2}
                      alignContent={"flex-start"}
                      height={625}
                      sx={{ overflowY: "scroll" }}
                    >
                      {values.questions.map((q, i) => (
                        <Grid
                          size={{
                            xs:
                              q.answerType.id === AnswerTypes.Multiline ||
                              q.answerType.id === AnswerTypes.WeeklyTask ||
                              q.answerType.id === AnswerTypes.Section ||
                              q.answerType.id === AnswerTypes.Confidential ||
                              q.answerType.id === AnswerTypes.Site ||
                              q.answerType.id === AnswerTypes.Dates
                                ? 12
                                : 6,
                          }}
                          key={i}
                        >
                          <QuestionPreview
                            question={q}
                            index={i}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
                <Base.Modal
                  open={statusModal}
                  onClose={() => {
                    setStatusModal(false);
                    setFieldValue("statusAdd", undefined);
                  }}
                >
                  <FieldArray
                    name="statuses"
                    render={({ move }) => (
                      <ManageJobTypeStatusForm
                        values={values.statuses}
                        handleAdd={(newStatus) => {
                          if (newStatus.id > 0) {
                            const index = values.statuses.findIndex(
                              (obj) => obj.id === newStatus.id,
                            );
                            values.statuses[index] = newStatus;
                            setFieldValue("statuses", values.statuses);
                          } else {
                            setFieldValue("statuses", [
                              ...values.statuses,
                              newStatus,
                            ]);
                          }
                        }}
                        handleCancel={() => setStatusModal(false)}
                        renderOption={renderOption}
                        handleRemove={(s) => {
                          if (s.id === 0) {
                            const filtered = values.statuses.filter(
                              (x) => x.name !== s.name || x.order !== s.order,
                            );
                            setFieldValue("statuses", filtered);
                          } else {
                            const filtered = values.statuses.filter(
                              (x) => x.id !== s.id,
                            );
                            setFieldValue("statuses", filtered);
                          }
                        }}
                        move={move}
                      />
                    )}
                  />
                </Base.Modal>
                <Base.Modal
                  open={colorModal}
                  onClose={() => setColorModal(false)}
                >
                  <Paper sx={{ paddingX: 5, paddingY: 2 }}>
                    <HexColorPicker color={color} onChange={setColor} />
                    <Grid
                      container
                      direction={"row"}
                      justifyContent={"space-between"}
                      mt={2}
                    >
                      <Grid>
                        <Base.Button
                          text="Cancel"
                          onClick={() => setColorModal(false)}
                        />
                      </Grid>
                      <Grid>
                        <Base.Button
                          text="Save"
                          onClick={() => {
                            setFieldValue("color", color);
                            setColorModal(false);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Base.Modal>
                <Base.Modal
                  open={multipleChoiceIndex >= 0}
                  onClose={() => setMultipleChoiceIndex(-1)}
                >
                  <ManageJobTypeKeyValueOptionForm
                    index={multipleChoiceIndex}
                    values={values}
                    handleAdd={() => {
                      setFieldValue(
                        `questions[${multipleChoiceIndex}].choices`,
                        [
                          ...values.questions[multipleChoiceIndex].choices,
                          { id: 0, value: values.add },
                        ],
                      );
                      setFieldValue("add", "");
                      if (status === multipleChoiceError) {
                        setStatus(undefined);
                      }
                    }}
                    handleClose={() => {
                      setMultipleChoiceIndex(-1);
                      setFieldValue("add", "");
                    }}
                    handleRemove={(s) => {
                      const filtered = values.questions[
                        multipleChoiceIndex
                      ].choices.filter((f) => f.value !== s.value);
                      setFieldValue(
                        `questions[${multipleChoiceIndex}].choices`,
                        filtered,
                      );
                    }}
                  />
                </Base.Modal>
              </Form>
            )}
          </Formik>
        )}
      </Paper>
    </Container>
  );
};

export default EditJobTypePage;
